<template>
    <o-recepient @showModal="showModal">
        <template v-slot:header>
            <div class="modal-header-title">
                <i class="fas fa-receipt"></i> {{ item.title }}
            </div>
        </template>
        <template v-slot:body>
            <div class="desktop-receipt-container">
            <div class="custom-modal-content receipt-redesign">
                <o-form autocomplete="off">
                    <div v-if="errors.length">
                        <div class="alert alert-danger">
                            <p>
                                <b>Please correct the following error(s):</b>
                                <ul>
                                    <li v-for="(err, index) in errors" :key="index">{{ err }}</li>
                                </ul>
                            </p>
                        </div>
                    </div>
                    
                    <div id="receipt-content" class="receipt-wrapper">
                        <!-- Print Header (Hidden on Web) -->
                        <div class="print-header d-none">
                            <h2 class="print-title">{{ printTitle }}</h2>
                            <div class="print-divider"></div>
                        </div>

                        <!-- Top Card: Company & Transaction Info -->
                        <div class="receipt-card top-info-card">
                            <div class="company-info-col">
                                <div class="company-logo-wrapper">
                                    <img :src="item.business_logo" crossorigin="anonymous" class="company-logo-img" alt="Company Logo" @error="handleImageError" />
                                </div>
                                <div class="company-details">
                                    <template v-if="item.merchant_name || item.branch_name">
                                        <h4 class="company-name">{{ item?.branch_name }}</h4>
                                        <p class="company-sub">{{ item?.merchant_name }}</p>
                                    </template>
                                    <template v-else>
                                        <h4 class="company-name">{{ item?.corporate_name }}</h4>
                                    </template>
                                </div>
                            </div>
                            
                            <div class="divider-vertical"></div>
                            
                            <div class="transaction-info-col">
                                <div class="info-row">
                                    <span class="info-label">Transaction Code</span>
                                    <span class="info-colon">:</span>
                                    <span class="info-value">{{ item.transaction_code || '-' }}</span>
                                </div>
                                <div class="info-row">
                                    <span class="info-label">Transaction Date</span>
                                    <span class="info-colon">:</span>
                                    <span class="info-value">{{ item.created_at }}</span>
                                </div>
                                <div class="info-row">
                                    <span class="info-label">Reference Code</span>
                                    <span class="info-colon">:</span>
                                    <span class="info-value">{{ item.reference_code || '-' }}</span>
                                </div>
                                <div class="info-row align-items-center">
                                    <span class="info-label">Transaction Type</span>
                                    <span class="info-colon">:</span>
                                    <span class="info-value">
                                        <span class="badge-type">{{ transactionTypeDisplay }}</span>
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- Member Information -->
                        <div class="receipt-section">
                            <h5 class="section-title mb-1">
                                <i class="far fa-user-circle"></i> MEMBER INFORMATION
                            </h5>
                            <div class="receipt-card member-card">
                                <div class="member-grid">
                                    <div class="member-grid-item">
                                        <div class="grid-icon"><i class="far fa-user"></i></div>
                                        <div class="grid-data">
                                            <span class="data-label">Name</span>
                                            <span class="data-value">{{ item.member_name }}</span>
                                        </div>
                                    </div>
                                    <div class="member-grid-item">
                                        <div class="grid-icon"><i class="far fa-envelope"></i></div>
                                        <div class="grid-data">
                                            <span class="data-label">Email</span>
                                            <span class="data-value">{{ item.member_email }}</span>
                                        </div>
                                    </div>
                                    <div class="member-grid-item">
                                        <div class="grid-icon"><i class="fas fa-phone-alt"></i></div>
                                        <div class="grid-data">
                                            <span class="data-label">Phone</span>
                                            <span class="data-value">{{ item.phone }}</span>
                                        </div>
                                    </div>
                                    <div class="member-grid-item">
                                        <div class="grid-icon"><i class="fas fa-map-marker-alt"></i></div>
                                        <div class="grid-data">
                                            <span class="data-label">Address / Country</span>
                                            <span class="data-value">{{ item.address }}, {{ item.state }} / {{ countryName }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- Transaction Details -->
                        <div class="receipt-section">
                            <h5 class="section-title mb-1">
                                <i class="far fa-file-alt"></i> TRANSACTION DETAILS
                            </h5>
                            <div class="receipt-card table-card">
                                <table class="receipt-table">
                                    <thead>
                                        <tr>
                                            <th>DETAILS</th>
                                            <th>TYPE</th>
                                            <th>{{ qtyHeaderDisplay }}</th>
                                            <th>UOM</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>
                                                <div class="detail-cell">
                                                    <div class="detail-text-wrapper">
                                                        <div class="detail-name">
                                                            <template v-if="typeof item.detail_name === 'string' && item.detail_name.includes('$')">
                                                                <span v-for="(detail, index) in formattedDetail(item.detail_name)" :key="index" class="d-block">
                                                                    {{ detail }}
                                                                </span>
                                                            </template>
                                                            <template v-else>
                                                                {{ item.detail_name }}
                                                            </template>
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>{{ formatItemType(item.type) }}</td>
                                            <td>
                                                <template v-if="!isPointTransaction && typeof item.qty === 'string' && item.qty.includes('$')">
                                                    <span v-for="(detail, index) in formattedDetail(item.qty)" :key="index" class="d-block">
                                                        {{ detail }}
                                                    </span>
                                                </template>
                                                <template v-else>
                                                    {{ displayQty }}
                                                </template>
                                            </td>
                                            <td class="font-weight-bold dynamic-text-color">{{ displayUom }}</td>
                                        </tr>
                                        <tr v-if="!isPointTransaction && item.point > 0">
                                            <td colspan="2" class="font-weight-bold">{{ pointEarnedLabel }}</td>
                                            <td class="font-weight-bold" :style="pointEarnedStyle">{{ pointEarnedDisplay }}</td>
                                            <td class="font-weight-bold dynamic-text-color">POIN</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <!-- Summary -->
                        <div class="receipt-section">
                            <h5 class="section-title mb-1">
                                <i class="fas fa-chart-bar"></i> SUMMARY
                            </h5>
                            <div class="receipt-card table-card summary-card">
                                <table class="receipt-table summary-table">
                                    <thead>
                                        <tr>
                                            <th>DESCRIPTION</th>
                                            <th>AMOUNT</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr v-if="!isPointTransaction">
                                            <td class="font-weight-bold">Subtotal</td>
                                            <td class="amount-value">{{ subtotalDisplay }}</td>
                                        </tr>
                                        <tr v-else-if="hasVoucherDiscount">
                                            <td class="font-weight-bold">Subtotal</td>
                                            <td class="amount-value">{{ displayBeforeVoucher }}</td>
                                        </tr>
                                        <tr v-if="hasVoucherDiscount">
                                            <td class="font-weight-bold">Voucher Discount</td>
                                            <td class="amount-value text-danger">- {{ displayVoucherDiscount }}</td>
                                        </tr>                                        
                                        <tr v-if="showTaxRow">
                                            <td class="font-weight-bold">Tax ({{ percentageFormat(displayTaxCharge) }})</td>
                                            <td class="amount-value">{{ formattedTaxValue }}</td>
                                        </tr>

                                        <tr v-if="showServiceRow">
                                            <td class="font-weight-bold">Service Charge ({{ percentageFormat(displayServiceCharge) }})</td>
                                            <td class="amount-value">{{ formattedServiceValue }}</td>
                                        </tr>
                                        
                                        <tr>
                                            <td class="font-weight-bold">{{ totalLabel }}</td>
                                            <td class="amount-value">{{ totalValue }}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>

                        <div class="thank-you-footer">
                            <div class="check-icon"><i class="fas fa-check"></i></div>
                            <div class="thank-you-text">
                                <p class="mb-0 font-weight-bold">Thank you for your transaction.</p>
                                <p class="mb-0 text-muted">Please present this copy to the merchant.</p>
                            </div>
                        </div>
                    </div>
                    
                    <div class="action-buttons mt-4 d-flex">
                        <button class="btn btn-success print-btn me-2" @click.prevent="printReceipt" type="button" :disabled="isPdfLoading">
                            <span v-if="isPdfLoading" class="pdf-spinner me-2"></span>
                            <i v-else class="fas fa-download me-2"></i>
                            {{ isPdfLoading ? 'Downloading...' : 'Download Receipt' }}
                        </button>
                        <button class="btn btn-outline-success close-btn" @click.prevent="closeModal" type="button" :disabled="isPdfLoading">
                            Close
                        </button>
                    </div>
                </o-form>
            </div>
            </div>

            <!-- Mobile Receipt Component -->
            <member-transaction-mobile
                :item="item"
                :isPdfLoading="isPdfLoading"
                @closeModal="closeModal"
                @printReceipt="printReceipt"
            />
        </template>
    </o-recepient>
</template>

<script>
import { ORecepient, OForm } from '@/components/OmnihControl';
import MemberTransactionMobile from './MemberTransactionMobile.vue';
import { mapState } from 'vuex';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

const toNumber = (value) => parseFloat(value) || 0;
export default {
    name: 'MemberTransaction',
    components: {
        ORecepient,
        OForm,
        MemberTransactionMobile,
    },
    props: [
        'editedItem',
        'validationdetail',
    ],
    data() {
        return {
            item: {
                reference_code: '',
                guest_name: '',
                country: '',
                phone: '',
                email: '',
                title: 'My Transaction',
            },
            validationDetail: this.validationdetail,
            isModalVisible: false,
            errors: [],
            countryName: this.editedItem?.country || '',
            isPdfLoading: false,
        }
    },
    watch: {
        editedItem: {
            handler(val) {
                if (val && Object.keys(val).length !== 0) {
                    this.item = { ...val };
                    this.countryName = this.item.country || '';
                    this.fetchCountryName();
                }
            },
            immediate: true,
            deep: true
        }
    },
    mounted() {        
        if (Object.keys(this.editedItem).length !== 0) {
            this.item = { ...this.editedItem };
            console.log('data',this.editedItem);
            this.countryName = this.item.country || '';
        }
        this.fetchCountryName();
    },
    methods: {
        showModal() {
            this.isModalVisible = true;
        },
        closeModal(e) {
            e?.preventDefault?.();
            this.$emit('parentCloseModal');
        },
        handleImageError(event) {
            if (this.item.fallback_logo && event.target.src !== this.item.fallback_logo) {
                event.target.src = this.item.fallback_logo;
            }
        },
        async fetchCountryName() {
            if (!this.item.country) return;
            try {
                const response = await fetch('https://s3.ap-southeast-1.amazonaws.com/cdn.omnihotelier.com/assets/json/countries-2.json');
                const countries = await response.json();
                const country = countries.find(c => c.id == this.item.country);
                if (country) {
                    this.countryName = country.name;
                }
            } catch (err) {
                console.error("Failed to fetch countries", err);
            }
        },
        async printReceipt() {
            const receiptElement = document.getElementById('receipt-content');
            if (!receiptElement) return;
            this.isPdfLoading = true;
            try {
                // Ensure all fonts and images are loaded
                const canvas = await html2canvas(receiptElement, {
                    scale: 0.9, // Higher resolution
                    useCORS: true, // Handle cross-origin images
                    backgroundColor: '#ffffff',
                    windowWidth: 1200, // Force desktop viewport size for media queries
                    onclone: (clonedDoc) => {
                        const desktopContainer = clonedDoc.querySelector('.desktop-receipt-container');
                        if (desktopContainer) {
                            desktopContainer.style.display = 'block';
                            desktopContainer.classList.remove('d-none');
                        }
                        const clonedReceipt = clonedDoc.getElementById('receipt-content');
                        if (clonedReceipt) {
                            // Force desktop width
                            clonedReceipt.style.display = 'block';
                            clonedReceipt.classList.remove('d-none');
                            clonedReceipt.style.width = '800px';
                            clonedReceipt.style.maxWidth = 'none'; // Overide any max-width
                            clonedReceipt.style.padding = '20px'; // Add padding for safety
                            
                            // Show print header
                            const printHeader = clonedReceipt.querySelector('.print-header');
                            if (printHeader) {
                                printHeader.classList.remove('d-none');
                                printHeader.style.display = 'block';
                            }
                        }
                    }
                });

                const imgData = canvas.toDataURL('image/png');
                // A4 dimensions: 210 x 297 mm
                const pdf = new jsPDF({
                    orientation: 'portrait',
                    unit: 'mm',
                    format: 'a4'
                });

                // Margins and Image Dimensions
                const marginX = 10; // 10mm left and right margin
                const marginY = 15; // 15mm top margin
                const maxPdfWidth = 210 - (marginX * 2);
                const maxPdfHeight = 297 - (marginY * 2);

                let pdfWidth = maxPdfWidth;
                let pdfHeight = (canvas.height * pdfWidth) / canvas.width;

                // If height exceeds page, scale down to fit height
                if (pdfHeight > maxPdfHeight) {
                    pdfHeight = maxPdfHeight;
                    pdfWidth = (canvas.width * pdfHeight) / canvas.height;
                }

                // Center horizontally
                const finalX = marginX + (maxPdfWidth - pdfWidth) / 2;

                pdf.addImage(imgData, 'PNG', finalX, marginY, pdfWidth, pdfHeight);

                const fileName = `Transaction_Receipt_${this.item.transaction_code || 'Detail'}.pdf`;
                const isNative = window.Capacitor?.isNativePlatform?.() ?? false;

                if (isNative) {
                    const { Filesystem, Directory } = await import('@capacitor/filesystem');
                    const { Share } = await import('@capacitor/share');
                    const base64Data = pdf.output('datauristring').split(',')[1];
                    const result = await Filesystem.writeFile({
                        path: fileName,
                        data: base64Data,
                        directory: Directory.Cache,
                    });
                    // Use Share API to open/share PDF — window.open(file://) is blocked on Android WebView
                    await Share.share({
                        title: `Transaction Receipt`,
                        url: result.uri,
                        dialogTitle: 'Open or Share PDF Receipt',
                    });
                } else {
                    pdf.save(fileName);
                }
            } catch (error) {
                console.error("Error generating PDF", error);
            } finally {
                this.isPdfLoading = false;
            }
        },

        priceFormat(value) {
            if (value == null || value === '') {
                return '-';
            }

            let isNegative = false;

            if (typeof value === 'number') {
                if (value < 0) {
                    isNegative = true;
                    value = Math.abs(value);
                }
            } else if (typeof value === 'string') {
                if (value.trim().startsWith('-')) {
                    isNegative = true;
                    value = value.trim().slice(1);
                }
            }

            let formatted = value.toString().replace(/[^0-9.,]/g, '');
            let parts = formatted.split(/[.,]/);
            let mainNumber = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
            let decimalPart = parts[1] && parts[1] !== '00' ? ',' + parts[1] : '';

            const finalResult = (isNegative ? '-' : '') + mainNumber + decimalPart;
            return finalResult;
        },

        percentageFormat(value) {
            if (value == null || value === '') {
                return '-';
            }

            const numericValue = Number(value);
            if (Number.isNaN(numericValue)) {
                return '-';
            }

            return `${numericValue.toLocaleString('id-ID', {
                maximumFractionDigits: 2,
            })}%`;
        },

        formattedDetail(detail) {
            if (detail.includes('$')) {
                return detail.split('$');
            }
            return [detail];
        },

        formatItemType() {
            return this.dynamicTransactionTitle;
        }
    },
    computed: {
        ...mapState({}),
        isPointTransaction() {
            return (!this.item.transaction_value || this.item.transaction_value == 0) && this.item.point !== null && this.item.point !== undefined;
        },
        isPending() {
            return Number(this.item.is_pending) === 1 || this.item.is_pending === true || this.item.is_pending === 'true';
        },
        formattedPointValue() {
            if (this.item.point === undefined || this.item.point === null) return '0';
            const num = parseFloat(this.item.point);
            if (isNaN(num)) return String(this.item.point);
            const formatted = Math.abs(num).toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1.');
            const detailNameLower = (this.item.detail_name || '').toLowerCase();
            const type = (this.item.type || '').toLowerCase();
            const txCode = (this.item.transaction_code || this.item.code || '');

            if (this.isPending) {
                return formatted;
            }
            if (type === 'expiration' || detailNameLower.includes('expired') || detailNameLower.includes('expiration') || txCode.startsWith('EXP-')) {
                return `-${formatted}`;
            }
            if ((type === 'transaction' && !detailNameLower.includes('reedem') && !detailNameLower.includes('deduction') && num >= 0) || type === 'cancel') {
                return `+${formatted}`;
            }
            if (type === 'redeem' || (type === 'transaction' && (detailNameLower.includes('reedem') || detailNameLower.includes('deduction') || num < 0))) {
                return `-${formatted}`;
            }
            return num >= 0 ? `+${formatted}` : `-${formatted}`;
        },
        pointEarnedLabel() {
            return this.isPending ? 'Points Pending' : 'Points Earned';
        },
        pointEarnedDisplay() {
            return this.formattedPointValue;
        },
        pointEarnedStyle() {
            if (this.isPending) {
                return { color: '#6B7280' };
            }
            return { color: '#00AA00' };
        },
        displayQty() {
            if (this.isPointTransaction) return this.formattedPointValue;
            return this.item.qty;
        },
        displayUom() {
            if (this.isPointTransaction) return 'POINT';
            if (this.item.type === 'transaction') return 'PRODUCT';
            return '-';
        },
        displayBeforeVoucher(){
            return this.priceFormat(this.item.transaction_value);
        },
        displayAmount() {            
            if (this.isPointTransaction) return `${this.formattedPointValue} POIN`;
            if (this.hasVoucherDiscount && this.item.amount_after_voucher !== undefined && this.item.amount_after_voucher !== null) {
                return this.priceFormat(this.item.amount_after_voucher);
            }
            return this.priceFormat(this.item.transaction_value);
        },
        hasVoucherDiscount() {
            return !this.isPointTransaction && this.item.voucher_discount && parseFloat(this.item.voucher_discount) > 0;
        },
        displayGrossAmount() {
            return this.priceFormat(this.item.transaction_value);
        },
        displayVoucherDiscount() {
            return this.priceFormat(this.item.voucher_discount);
        },
        displayBeforeTaxService() {
            return this.priceFormat(this.item.before_tax_service);
        },
        displayAfterTaxService() {
            return this.priceFormat(this.item.after_tax_service);
        },
        /* ---------- Subtotal ---------- */

        // Tax dan service sama-sama punya nilai > 0
        // kalau hanya salah satu yang ada, subtotal jatuh ke displayAmount.
        hasTaxAndServiceValue() {
            return toNumber(this.displayTaxValue) > 0 && toNumber(this.displayServiceValue) > 0;
        },

        subtotalDisplay() {
            if (this.hasVoucherDiscount) return this.displayBeforeVoucher;
            return this.hasTaxAndServiceValue ? this.displayBeforeTaxService : this.displayAmount;
        },

        /* ---------- Tax & Service ---------- */

        // Satu aturan untuk tax dan service
        // Tampil jika: ada voucher, atau amount_after_voucher kosong, atau lebih kecil dari transaction_value.
        showTaxServiceBreakdown() {
            if (this.hasVoucherDiscount) return true;

            const afterVoucher = this.item.amount_after_voucher;            

            return afterVoucher < this.item.transaction_value;
        },

        displayTaxCharge() {
            return this.showTaxServiceBreakdown ? this.item.tax_charge : 0;
        },
        displayServiceCharge() {
            return this.showTaxServiceBreakdown ? this.item.service_charge : 0;
        },

        showTaxRow() {
            return toNumber(this.displayTaxCharge) > 0;
        },
        showServiceRow() {
            return toNumber(this.displayServiceCharge) > 0;
        },

        formattedTaxValue() {
            return this.displayTaxValue ? this.priceFormat(this.displayTaxValue) : 0;
        },
        formattedServiceValue() {
            return this.displayServiceValue ? this.priceFormat(this.displayServiceValue) : 0;
        },

        /* ---------- Total ---------- */
        totalLabel() {
            if (this.isPointTransaction) return 'Total Processed';

            const { tax_value, service_value, before_tax_service, after_tax_service, transaction_value } = this.item;
            
            const hasTaxAndService = toNumber(tax_value) > 0 && toNumber(service_value) > 0;
            const beforeDiffersFromSubtotal = toNumber(before_tax_service) !== toNumber(transaction_value);
            const afterWithinSubtotal = toNumber(after_tax_service) <= toNumber(transaction_value);            
            const afterWithDiscount = this.hasVoucherDiscount && afterWithinSubtotal ? true : !this.hasVoucherDiscount && afterWithinSubtotal ? true : false;
            const isTaxServiceIncluded =
                hasTaxAndService &&
                beforeDiffersFromSubtotal &&
                afterWithDiscount;
            return isTaxServiceIncluded
                ? 'Total Payment (Tax & Service Included)'
                : 'Total Payment';
        },        
        totalValue() {
            return this.isPointTransaction ? this.displayAmount : this.displayTotalPayment;
        },

        // MEM 1115
        displayTotalPayment() {
            const {
                tax_charge,
                service_charge,
                after_tax_service,
                amount_after_voucher,
                transaction_value,
            } = this.item;

            const hasTaxAndService = toNumber(tax_charge) > 0 && toNumber(service_charge) > 0;

            if (hasTaxAndService) {
                
                if (after_tax_service != null) {
                    return this.priceFormat(after_tax_service);
                }
                if (this.hasVoucherDiscount && amount_after_voucher != null) {
                    return this.priceFormat(amount_after_voucher);
                }
            }else if (this.hasVoucherDiscount && amount_after_voucher != null) {
                return this.priceFormat(amount_after_voucher);
            }

            return this.priceFormat(transaction_value);
        },        
        displayTaxValue() {
            return this.item.tax_value;
        },
        displayServiceValue() {
            return this.item.service_value;
        },
        transactionTypeDisplay() {
            return this.dynamicTransactionTitle;
        },
        qtyHeaderDisplay() {
            if (this.isPointTransaction || (this.item && this.item.type !== 'transaction')) {
                return 'POINTS';
            }
            return 'QTY';
        },
        dynamicTransactionTitle() {
            if (!this.item) return 'Transaction';

            const typeRaw = this.item.type || '';
            const type = typeRaw ? typeRaw.charAt(0).toUpperCase() + typeRaw.slice(1) : '';
            const detailRaw = this.item.detail_name;

            if (typeRaw.toLowerCase() === 'gift') {
                return detailRaw ? `Gift - ${detailRaw}` : 'Gift Voucher';
            }

            const checkDetail = (detailRaw || '').toLowerCase();
            const checkCode = (this.item.transaction_code || this.item.code || '');
            const typeLower = typeRaw.toLowerCase();

            if (typeLower === 'expiration' || checkDetail.includes('expired') || checkDetail.includes('expiration') || checkCode.startsWith('EXP-')) {
                return 'Expiration';
            }

            if (detailRaw === 'Top Up') {
                return detailRaw;
            }

            if (detailRaw === 'Deduction Point' || detailRaw == null) {
                return 'Deduction Point';
            }

            if (typeLower === 'transaction' || typeLower === 'redeem' || typeLower === 'cancel') {
                return type || 'Transaction';
            }

            const detailParts = (detailRaw || '')
                .split('$')
                .map(part => part.trim())
                .filter(Boolean);

            if (!detailParts.length) {
                return type ? `${type} - Deduction Point` : 'Deduction Point';
            }

            const [firstDetail, ...restDetails] = detailParts;
            const extraCount = restDetails.length;
            const detailText = extraCount ? `${firstDetail} +${extraCount}` : firstDetail;

            return type ? `${type} - ${detailText}` : detailText;
        },
        printTitle() {
            if (!this.item.title) return 'TRANSACTION RECEIPT';
            const title = this.item.title.toUpperCase();
            return title.startsWith('MY ') ? title.substring(3) : title;
        },
    }
}
</script>

<style scoped>
.modal-header-title {
    font-size: 1.25rem;
    font-weight: 700;
    color: var(--primary-color);
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
}

.receipt-redesign {
    background-color: #f7f9f8;
    padding: 16px;
    border-radius: 0;
    width: 100%;
    min-height: 100%;
    font-family: 'Inter', Arial, sans-serif;
}

.receipt-wrapper {
    display: flex;
    flex-direction: column;
    gap: 16px;
}

.print-header {
    text-align: center;
    margin-bottom: 10px;
}

.print-title {
    color: var(--primary-color);
    font-size: 24px;
    font-weight: 800;
    letter-spacing: 1px;
    margin: 0;
}

.print-divider {
    width: 60px;
    height: 4px;
    background-color: var(--secondary-color);
    margin: 12px auto 0;
    border-radius: 2px;
}

.receipt-card {
    background: #ffffff;
    border: 1px solid #eaeaea;
    border-radius: 8px;
    overflow: hidden;
}

.top-info-card {
    display: flex;
    flex-wrap: wrap;
    padding: 20px;
    gap: 20px;
}

.company-info-col {
    flex: 1;
    min-width: 300px;
    display: flex;
    gap: 16px;
    align-items: center;
}

.company-logo-wrapper {
    width: 120px;
    flex-shrink: 0;
}

.company-logo-img {
    width: 100%;
    height: auto;
    border-radius: 4px;
    object-fit: contain;
}

.company-name {
    margin: 0 0 4px 0;
    font-size: 18px;
    font-weight: 700;
    color: #333;
}

.company-sub {
    font-size: 16px;
    color: #666;
    margin-bottom: 12px;
}

.contact-item {
    font-size: 13px;
    color: #555;
    margin-bottom: 6px;
    display: flex;
    align-items: flex-start;
    gap: 8px;
}

.contact-item i {
    margin-top: 3px;
    width: 14px;
}

.divider-vertical {
    width: 1px;
    background: #eaeaea;
    margin: 0 10px;
}

.transaction-info-col {
    flex: 1;
    min-width: 250px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    justify-content: center;
}

.info-row {
    display: flex;
    font-size: 13px;
}

.info-label {
    width: 130px;
    color: #555;
    font-weight: 500;
    font-size: 12px;
}

.info-colon {
    width: 15px;
    color: #555;
}

.info-value {
    flex: 1;
    font-weight: 600;
    color: #333;
    font-size: 12px;
}

.badge-type {
    background-color: var(--secondary-color);
    color: var(--primary-color);
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 11px;
    font-weight: 700;
}

.receipt-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.section-title {
    font-size: 13px;
    font-weight: 700;
    color: var(--primary-color);
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
}

.section-title i {
    background: var(--primary-color);
    color: white;
    width: 24px;
    height: 24px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    font-size: 12px;
}

.member-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(160px, 1fr));
}

.member-grid-item {
    display: flex;
    gap: 12px;
    padding: 16px;
    border-right: 1px solid #eaeaea;
    border-bottom: 1px solid #eaeaea;
}

.member-grid-item:last-child {
    border-right: none;
}

.grid-icon {
    color: var(--primary-color);
    font-size: 16px;
    padding-top: 2px;
}

.grid-data {
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.data-label {
    font-size: 11px;
    color: #666;
}

.data-value {
    font-size: 12px;
    font-weight: 600;
    color: #333;
    word-break: break-word;
}

.receipt-table {
    width: 100%;
    border-collapse: collapse;
}

.receipt-table th {
    background-color: var(--primary-color);
    color: white;
    padding: 12px 16px;
    font-size: 12px;
    font-weight: 600;
    text-align: left;
}

.receipt-table td {
    padding: 16px;
    font-size: 12px;
    border-bottom: 1px solid #eaeaea;
    vertical-align: middle;
}

.detail-cell {
    display: flex;
    align-items: center;
    gap: 12px;
}

.detail-icon-wrapper {
    background: var(--secondary-color);
    color: var(--primary-color);
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
}

.detail-name {
    font-weight: 600;
    color: #333;
}

.detail-sub {
    font-size: 12px;
    color: #666;
    margin-top: 2px;
}

.summary-table th {
    background-color: var(--secondary-color);
    color: var(--primary-color);
}

.amount-value {
    color: #111827;
    font-weight: 700;
    font-size: 14px;
}

.dynamic-text-color {
    color: var(--primary-color);
}

.thank-you-footer {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    margin-top: 16px;
    padding-top: 16px;
    border-top: 1px dashed #ccc;
    text-align: center;
}

.check-icon {
    background: var(--secondary-color);
    color: var(--primary-color);
    width: 32px;
    height: 32px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
}

.thank-you-text {
    text-align: left;
    font-size: 12px;
}

.action-buttons {
    display: flex;
    justify-content: flex-start;
}

.print-btn {
    background-color: var(--primary-color);
    border-color: var(--primary-color);
    color: white;
}

.print-btn:hover {
    background-color: var(--primary-color);
    border-color: var(--primary-color);
    opacity: 0.9;
}

.close-btn {
    color: var(--primary-color);
    border-color: var(--primary-color);
}

.close-btn:hover {
    background-color: var(--primary-color);
    color: white;
}

/* PDF loading spinner */
@keyframes spin {
    to { transform: rotate(360deg); }
}
.pdf-spinner {
    display: inline-block;
    width: 14px;
    height: 14px;
    border: 2px solid rgba(255,255,255,0.4);
    border-top-color: #ffffff;
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
    vertical-align: middle;
}
.print-btn:disabled {
    opacity: 0.75;
    cursor: not-allowed;
}
.close-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.receipt-redesign {
    width: 100%;
}

.header-styled {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 20px;
    border-bottom: 2px solid var(--primary-color);
    padding-bottom: 10px;
}

.header-styled i {
    font-size: 24px;
    color: var(--primary-color);
}


.desktop-receipt-container {
    display: block;
}
.mobile-receipt-container {
    display: none;
}


@media (max-width: 992px) {
    .top-info-card {
        flex-direction: column;
        align-items: stretch;
        padding: 16px;
        gap: 16px;
    }
    .company-info-col {
        width: 100%;
        min-width: 100%;
        justify-content: flex-start;
    }
    .divider-vertical {
        width: 100%;
        height: 1px;
        margin: 4px 0;
    }
    .transaction-info-col {
        width: 100%;
        min-width: 100%;
    }
    .member-grid {
        grid-template-columns: repeat(2, 1fr);
    }
    .member-grid-item {
        border-right: 1px solid #eaeaea;
        border-bottom: 1px solid #eaeaea;
    }
    .member-grid-item:nth-child(2n) {
        border-right: none;
    }
    .member-grid-item:nth-last-child(-n+2):nth-child(2n+1),
    .member-grid-item:nth-last-child(-n+2):nth-child(2n+1) ~ .member-grid-item {
        border-bottom: none;
    }
    .receipt-card {
        overflow-x: auto;
    }
    .receipt-table {
        min-width: 550px;
    }
    .summary-table {
        min-width: 350px;
    }
}



/* ==========================================================================
   Modal Entry Animation (matching Filter Modal in TransactionHistory.vue)
   ========================================================================== */
@keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
}
@keyframes slideUp {
    from { transform: translateY(100%); }
    to { transform: translateY(0); }
}
:deep(.modal.modal-fullscreen),
:deep(.modal-backdrop) {
    animation: fadeIn 0.2s ease-out !important;
}
:deep(.modal.modal-fullscreen .modal-content) {
    animation: slideUp 0.3s ease-out, fadeIn 0.3s ease-out !important;
}

/* ==========================================================================
   Small Devices (Tablets, Mobile Phones <= 768px)
   ========================================================================== */
@media (max-width: 768px) {
    /* Switch layout view from desktop to mobile card */
    .desktop-receipt-container {
        display: none !important;
    }
    .mobile-receipt-container {
        display: flex !important;
        flex-direction: column;
        padding: 16px 16px 48px 16px;
        min-height: 100%;
        height: auto !important;
        width: 100%;
        box-sizing: border-box;
        color: #333;
    }

    /* Override ORecepient modal hierarchy to guarantee scrollable flex viewport */
    :deep(.modal-fullscreen) {
        display: flex !important;
        align-items: stretch !important;
        justify-content: stretch !important;
        overflow: hidden !important;
        width: 100vw !important;
        height: 100vh !important;
        max-width: 100vw !important;
        max-height: 100vh !important;
        padding: 0 !important;
        margin: 0 !important;
        animation: fadeIn 0.2s ease-out !important;
    }
    :deep(.modal-fullscreen .modal-dialog) {
        width: 100% !important;
        height: 100% !important;
        max-width: 100% !important;
        max-height: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
        display: flex !important;
        flex-direction: column !important;
        flex: 1 1 auto !important;
        min-height: 0 !important;
        overflow: hidden !important;
    }
    :deep(.modal-fullscreen .modal-content) {
        background-color: var(--primary-color) !important;
        border: none !important;
        border-radius: 0 !important;
        box-shadow: none !important;
        width: 100% !important;
        height: 100% !important;
        max-height: 100% !important;
        margin: 0 !important;
        padding: 0 !important;
        display: flex !important;
        flex-direction: column !important;
        flex: 1 1 auto !important;
        min-height: 0 !important;
        overflow: hidden !important;
        animation: slideUp 0.3s ease-out, fadeIn 0.3s ease-out !important;
    }
    :deep(.modal-fullscreen .modal-body) {
        padding: 0 !important;
        margin: 0 !important;
        display: block !important;
        width: 100% !important;
        flex: 1 1 0% !important;
        min-height: 0 !important;
        max-height: 100% !important;
        overflow-y: auto !important;
        overflow-x: hidden !important;
        -webkit-overflow-scrolling: touch !important;
        background-color: var(--primary-color) !important;
    }
    :deep(.modal-header),
    :deep(.modal-footer) {
        display: none !important;
    }

    /* Fallback / Print adjustments for desktop-receipt-container below 768px */
    .top-info-card {
        flex-direction: column;
        align-items: center;
        text-align: center;
        padding: 16px;
    }
    .company-info-col {
        flex-direction: column;
        align-items: center;
        width: 100%;
        min-width: 100%;
    }
    .company-details {
        text-align: center;
    }
    .divider-vertical {
        width: 100%;
        height: 1px;
        margin: 8px 0;
    }
    .transaction-info-col {
        width: 100%;
        min-width: 100%;
    }
    .member-grid {
        grid-template-columns: 1fr;
    }

    .member-grid-item {
        border-right: none !important;
        border-bottom: 1px solid #eaeaea;
    }

    .member-grid-item:last-child {
        border-bottom: none;
    }

    .receipt-card {
        overflow-x: auto;
    }
    
    .receipt-table {
        min-width: 500px; /* Forces scroll on small screens */
    }
    
    .summary-table {
        min-width: 300px;
    }

    .action-buttons {
        flex-direction: column;
        width: 100%;
        gap: 8px;
    }
    
    .action-buttons button {
        width: 100%;
    }
}


</style>
